/* eslint-disable security/detect-non-literal-fs-filename */
import { call, put } from '@redux-saga/core/effects'
import { delay, race, select, take } from 'redux-saga/effects'
import { generatePath } from 'react-router'
import { AxiosError } from 'axios'
import dayjs from 'dayjs'
import { recordCollectionAction } from 'utils/collectionActions'
import {
  adjustEntityBalanceLocally,
  fetchEntityList,
  fetchLastActivity,
  showTxnAlertNudge
} from 'state/dashboard/dashboard.actions'
import { ICustomer, IMerchantProfile, ISupplier } from 'state/dashboard/dashboard.types'
import { Action } from 'infra/types'
import { createCustomerTransactionWithBills, deleteBillWithTxnIdReq } from 'services/Api/BillingApi'
import {
  removeStaffLinkTransaction,
  getRoutesAndBeats,
  updateRoutesAndBeats,
  getAccountTransactionsApi,
  getDoubleEntry
} from 'services/Api/StafflinkApi'
import { MerchantApi } from 'services/Api/MerchantApi'
import { getTruncatedString } from 'utils/getTruncatedString'
import { markDeleteAfterSettlement } from 'utils/settlementDelete'
import getWhatsappLink from 'utils/getWhatsappLink'
import { NotificationType } from 'pages/Notification/Notification.types'
import { addAutoFadeNotification } from 'pages/Notification/Notification.actions'
import { getErrorMessage } from 'services/Api/Errors'
import { Navigation } from 'services/navigation'
import { IDispatchCallback } from 'services/Api/types'
import { IAppState } from 'infra/AppState'
import { AuthStorageInstance } from 'services/Storage/AuthStorage'
import { Mixpanel } from 'services/Tracking/Mixpanel'
import { CustomerAndSupplierApi } from 'services/Api/CustomerAndSupplierApi'
import { CustomerSupplierReportApi } from 'services/Api/CustomerSupplierReportApi'
import { LedgerApi } from 'services/Api/LedgerApi'
import { getTransactionSyncService } from 'services/TransactionSync/TransactionSyncService'
import { getTransactionDatabaseManager } from 'services/IndexDB/TransactionDatabaseManager'
import { newPaths } from '../../routes'
import { forwardTo } from '../common'
import { ENTITY_TYPE, IApiResponse, API_RESPONSE_TYPE } from './../../constants'
import {
  ICreateTransaction,
  ITransactionAction,
  ITransactionBill,
  ITransactionFilter,
  REFERENCE_SOURCE,
  TRANSACTION_REPORT_ACTION,
  TRANSACTION_SORT_ORDER,
  TRANSACTION_TYPE,
  IUpdateRouteBeatAction,
  ITransactionsState,
  ROLE
} from './transaction.types'
import {
  getCreateCustomerBillTransactionPayload,
  getCreateCustomerTransactionPayload,
  getCreateSupplierTransactionPayload
} from './helpers/addTransaction'
import {
  addTransactionSuccess,
  generateTransactionReportFailure,
  generateTransactionReportSuccess,
  fetchReportTransactionsFailure,
  fetchReportTransactionsSuccess,
  getTransactionSuccess,
  fetchLatestTransactions as fetchLatestTransactionsAction,
  markTransactionDeletedLocally,
  TransactionsActions,
  addTransactionImageSuccess,
  deleteTransactionImageSuccess,
  setTransactionsLoading,
  addTransactionFailure,
  toggleEditTransactionAmount,
  setTransactionError,
  fetchAccountsRoutesAndBeatsSuccess,
  fetchAccountsRoutesAndBeats,
  setRoutesAndBeatsLoader,
  fetchLatestTransactionsSuccess,
  fetchLatestTransactionsFailure,
  saveDoubleEntryTransactionDetails,
  syncTransactionsWithIndexDB,
  syncTransactionsWithIndexDBSuccess,
  syncTransactionsWithIndexDBFailure,
  clearTransactionCache,
  refreshMerchantTransactionsSuccess,
  refreshMerchantTransactionsFailure
} from './transaction.actions'
import {
  getFormattedCustomerTransaction,
  getFormattedSupplierTransaction,
  getFormattedTransactionReport,
  formatLedgerTransactions
} from './helpers/formatter'
import { showReport } from './helpers/report'
function* patchCachedTransactionImages(
  entityType: ENTITY_TYPE,
  entityId: string,
  transactionId: string,
  patch: (images: ITransactionBill[]) => ITransactionBill[]
) {
  try {
    const dbManager = getTransactionDatabaseManager()
    yield call([dbManager, 'init'])
    const cached: any[] = yield call([dbManager, 'getTransactionsByEntity'], entityId, entityType)
    const existing = cached.find((t: any) => t?.id === transactionId)
    if (existing) {
      const images = patch(Array.isArray(existing.images) ? existing.images : [])
      yield call([dbManager, 'bulkUpsertTransactions'], entityType, [{ ...existing, images }])
    }
  } catch (cacheErr: any) {
    console.warn('[transactionImages] cache write-through failed:', cacheErr?.message)
  }
}

export function* addTransactionImageEffect(
  action: Action<{ image: Omit<ITransactionBill, 'id'>; entityType: ENTITY_TYPE; entityId: string }>
) {
  try {
    const { image, entityType, entityId } = action.payload
    const response: IApiResponse = yield call(CustomerAndSupplierApi.addTransactionImage, { image })
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      const saved: ITransactionBill = { ...image, ...(response.data || {}), id: response.data.id }
      yield call(
        patchCachedTransactionImages,
        entityType,
        entityId,
        image.transaction_id,
        (images) => (images.some((b) => b.id === saved.id) ? images : [...images, saved])
      )
      yield put(
        fetchLatestTransactionsAction({
          entityType,
          entityId
        })
      )
      yield put(
        addTransactionImageSuccess({
          transactionId: image.transaction_id,
          imageId: response.data.id
        })
      )
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e as AxiosError).message
      })
    )
  }
}

export function* removeTransactionImageEffect(
  action: Action<{
    imageId: string
    transactionId: string
    entityType: ENTITY_TYPE
    entityId: string
  }>
) {
  try {
    const { imageId, transactionId, entityType, entityId } = action.payload
    const response: IApiResponse = yield call(
      CustomerAndSupplierApi.removeTransactionImage,
      imageId
    )
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      yield call(patchCachedTransactionImages, entityType, entityId, transactionId, (images) =>
        images.filter((b) => b.id !== imageId)
      )
      yield put(
        fetchLatestTransactionsAction({
          entityType,
          entityId
        })
      )
      yield put(
        deleteTransactionImageSuccess({
          transactionId,
          imageId
        })
      )
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e as AxiosError).message
      })
    )
  }
}

export function* getTxnDataEffect(action: Action<string>) {
  try {
    const transactionId = action.payload
    const response: IApiResponse = yield call(
      CustomerAndSupplierApi.getTransactionData,
      transactionId
    )
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      yield put(getTransactionSuccess(getFormattedCustomerTransaction(response.data)))
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message
      })
    )
  }
}

export function* showNotificationForCollectionIdDeletion() {
  try {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: 'Transaction cannot be deleted as it is an online payment transaction'
      })
    )
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: e.message
      })
    )
  }
}

export function* sendReminderToCustomer(
  action: Action<{ customer: ICustomer; merchant: IMerchantProfile }>
) {
  const merchantMobile = action.payload.merchant.mobile
  const displayName = merchantMobile
    ? `${action.payload.merchant.name} (${merchantMobile})`
    : action.payload.merchant.name
  const dueAmount = Math.abs(action.payload.customer.balance)
  const isDue = action.payload.customer.balance <= 0
  // Same message Android sends (t_002_reminder_without_link / _advance): the balance, the
  // OkCredit app link (okcr.in/app), and the merchant "name (mobile)" signature.
  const message = isDue
    ? `Your balance of ₹${dueAmount} is Due.\nPlease pay at the earliest.\nokcr.in/app\n—\n${displayName}`
    : `Your balance of ₹${dueAmount} is Advance.\nokcr.in/app\n—\n${displayName}`
  try {
    yield window.open(getWhatsappLink(action.payload.customer.mobile, message))
    // Android SharePaymentReminder → RecordCollectionAction(WHATSAPP)
    recordCollectionAction(action.payload.merchant.id, action.payload.customer.id, 'WHATSAPP')
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: e.message
      })
    )
  }
}

export function* sendReminderToSupplier(
  action: Action<{ supplier: ISupplier; merchant: IMerchantProfile }>
) {
  const balance = Number(action.payload.supplier.balance)
  const balanceText =
    balance <= 0
      ? 'Credit:\nPayment reminder, please make payment as soon as possible.\n'
      : 'Advance:\nAccount update\n'
  const shopOwner = getTruncatedString(action.payload.merchant.name, 25)
  const amount = `${balance} ${balance <= 0 ? 'Due' : 'Advance'}`
  try {
    yield window.open(
      getWhatsappLink(
        action.payload.supplier.mobile,
        `Balance in ${balanceText}. Shop Owner: ${shopOwner}\n*Balance: ₹${amount}*\nVerified by OkCredit - Digital Udhaar Khata
      }`
      )
    )
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: e.message
      })
    )
  }
}

export function* fetchReportTransactionsEffect(
  action: Action<{
    entityType: ENTITY_TYPE
    entityId: string
    filter?: ITransactionFilter
  }>
) {
  const { entityType, entityId, filter } = action.payload
  try {
    let allTxns: any[] = []

    // Read straight from IndexedDB when cached — avoids the API call entirely.
    try {
      const dbManager = getTransactionDatabaseManager()
      yield call([dbManager, 'init'])
      const hasCache: boolean = yield call([dbManager, 'hasData'], entityId, entityType)
      if (hasCache) {
        allTxns = yield call([dbManager, 'getTransactionsByEntity'], entityId, entityType)
        // A report built from a stale cache shows a closing balance that contradicts
        // the account's current balance — recover the missing rows before building it.
        allTxns = yield call(healStaleAccountCache, entityId, entityType, allTxns)
      }
    } catch (cacheErr) {
      console.warn('[fetchReportTransactions] IndexedDB read failed, will fall back:', cacheErr)
    }

    // Cache miss (or read failed) → fall back to smart sync; populates cache for next time.
    if (allTxns.length === 0) {
      const { response } = yield call(fetchAllTransactions, {
        entityId,
        entityType,
        filter,
        orderBy: TRANSACTION_SORT_ORDER.BY_CREATION_TIME
      })
      if (
        response.type === API_RESPONSE_TYPE.SUCCESS &&
        response.data &&
        Object.keys(response.data).length > 0 &&
        response.data.list_data?.transactions
      ) {
        allTxns = response.data.list_data.transactions
      } else {
        yield put(
          addAutoFadeNotification({
            type: NotificationType.ERROR,
            bodyText: response.data?.message
          })
        )
        yield put(fetchReportTransactionsFailure(entityType, entityId))
        return
      }
    }

    // Apply date-range filter client-side since cache holds the full history.
    const filtered =
      filter?.startTime || filter?.endTime
        ? allTxns.filter((t: any) => {
            const billDate = Number(t.bill_date)
            if (filter?.startTime && billDate < filter.startTime) {
              return false
            }
            if (filter?.endTime && billDate > filter.endTime) {
              return false
            }
            return true
          })
        : allTxns
    const reportData = getFormattedTransactionReport(filtered, entityType)
    yield put(fetchReportTransactionsSuccess({ ...action.payload, data: reportData }))
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message
      })
    )
    yield put(fetchReportTransactionsFailure(entityType, entityId))
  }
}

export function* fetchAccountsRoutesAndBeatsEffect(action: Action<string>) {
  const accountId = action.payload
  try {
    const { Dashboard }: IAppState = yield select((state: IAppState) => state)
    const response: IApiResponse = yield call(getRoutesAndBeats, {
      userId: Dashboard.businessAccounts.individualUser?.id || '',
      accountId
    })
    if (response.type === API_RESPONSE_TYPE.SUCCESS && response.data) {
      if (response?.data?.accs?.length) {
        yield put(
          fetchAccountsRoutesAndBeatsSuccess({
            routes: response?.data?.accs?.[0]?.routes || [],
            beats: response?.data?.accs?.[0]?.beats || []
          })
        )
      }
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message || 'Unable to fetch routes and beats'
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message || 'Unable to fetch routes and beats'
      })
    )
  }
  yield put(setRoutesAndBeatsLoader(false))
}
export function* updateAccountsRoutesAndBeatsEffect(action: Action<IUpdateRouteBeatAction>) {
  const { accountId, addBeats, addRoutes, deleteBeats, deleteRoutes } = action.payload
  try {
    const { Dashboard }: IAppState = yield select((state: IAppState) => state)
    const response: IApiResponse = yield call(updateRoutesAndBeats, {
      data: [
        {
          id: accountId,
          ...(addBeats ? { add_beats: addBeats } : {}),
          ...(deleteBeats ? { delete_beats: deleteBeats } : {}),
          ...(addRoutes ? { add_routes: addRoutes } : {}),
          ...(deleteRoutes ? { delete_routes: deleteRoutes } : {})
        }
      ],
      userId: Dashboard.businessAccounts.individualUser?.id || ''
    })
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      yield put(fetchAccountsRoutesAndBeats(accountId))
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message
      })
    )
  }
}

export function addTransaction(
  action: Action<{
    entityType: ENTITY_TYPE
    entityId: string
    type: TRANSACTION_TYPE
    amount?: number
    source?: string
  }>
) {
  const { entityType, entityId, type, amount, source } = action.payload
  const query = new URLSearchParams()
  if (amount) {
    query.set('amount', String(amount))
  }
  if (source === 'settlement') {
    query.set('source', 'settlement')
  }
  const search = query.toString()
  Navigation().to(
    generatePath(newPaths.addTransaction, {
      entity: entityType,
      id: entityId,
      type
    }) + (search ? `?${search}` : '')
  )
}

export function* createTransaction(action: Action<ICreateTransaction & IDispatchCallback>) {
  const { entityType, entityId, redirectTo, billNumber, settlement } = action.payload
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER
  const { Experiment } = yield select((app: IAppState) => app)
  const { currentBusinessConfig } = yield select((app: IAppState) => app.Dashboard)
  try {
    const response: IApiResponse = isCustomer
      ? yield call(
          billNumber
            ? createCustomerTransactionWithBills
            : CustomerAndSupplierApi.createCustomerTransaction,
          billNumber
            ? getCreateCustomerBillTransactionPayload(action.payload)
            : getCreateCustomerTransactionPayload(action.payload)
        )
      : yield call(
          CustomerAndSupplierApi.createSupplierTransaction,
          entityId,
          getCreateSupplierTransactionPayload(action.payload)
        )
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      if (settlement) {
        // Delete → "Add Credit/Payment": pop back to the profile (no reload, no form left in
        // history for the delete saga's back() to land on) and reopen the delete prompt. The
        // balance is adjusted locally first so the prompt sees ₹0 before fetchEntityList
        // below confirms it. Same sign rule as deleteTransaction, reversed.
        const delta =
          Number(action.payload.transactionType) === TRANSACTION_TYPE.PAYMENT
            ? Number(action.payload.amount)
            : -Number(action.payload.amount)
        yield put(adjustEntityBalanceLocally({ entityType, entityId, delta }))
        markDeleteAfterSettlement(entityId)
        Navigation().back()
      } else if (redirectTo) {
        yield call(forwardTo, redirectTo)
      } else {
        const visibleScreens = currentBusinessConfig?.visible_screens
          ? currentBusinessConfig?.visible_screens
          : undefined
        const getDefaultRedirectUrl = () => {
          const url = Experiment.ab.staff_link ? newPaths.approve : newPaths.ledger
          if (!visibleScreens) {
            return url
          }
          return newPaths.approve
        }

        Navigation().replace(generatePath(getDefaultRedirectUrl()))
      }
      yield put(addTransactionSuccess(response.data))
      // Write-through: put the new txn into IndexedDB so the cache-only ledger read
      // shows it immediately (there is no API call on open to reconcile it).
      try {
        const created: any = response.data
        if (created && created.id) {
          const dbManager = getTransactionDatabaseManager()
          yield call([dbManager, 'init'])
          yield call([dbManager, 'bulkUpsertTransactions'], entityType, [
            {
              ...created,
              account_id: created.account_id || entityId,
              // The cache-read formatter (getFormatted{Customer,Supplier}Transaction)
              // expects the GetTransactions raw shape: `amount` in PAISE and a numeric
              // `type` (CREDIT=1, PAYMENT=2). The create-endpoint responses don't match
              // it — the supplier response carries no `type` (request uses `payment:
              // boolean`) so it rendered as NaN → collapsed onto the CREDIT side, and
              // the customer response's `amount` isn't in paise so ₹12 rendered as
              // ₹0.12. Both self-corrected only after a refresh replaced the row.
              // Override both from the local payload (authoritative and unit-correct)
              // so the optimistic write-through matches the fetched shape exactly.
              type: action.payload.transactionType,
              amount: Number((action.payload.amount * 100).toFixed(0)),
              // The ledger now renders the row time from create_time (see
              // Customer/SupplierTransactions). Guarantee it's set so a just-added
              // optimistic row shows a real time, not "Invalid Date", if the create
              // response omits it. Server overwrites this on the next refresh.
              create_time: created.create_time || Math.floor(Date.now() / 1000),
              // Never let a local write move the delta cursor (max cached update_time):
              // jumping it to "now" skips anything another device saved just before and
              // this tab hasn't pulled yet. The next delta re-delivers this row with the
              // server's update_time (same rule as softDeleteCachedTransaction).
              update_time: undefined
            }
          ])
        }
      } catch (cacheErr) {
        console.warn('[createTransaction] cache write-through failed:', cacheErr)
      }
      yield put(
        fetchLatestTransactionsAction({
          entityType,
          entityId
        })
      )
      yield put(fetchEntityList(entityType))
      // iOS/Android parity (AddTransactionScreenModel): right after the add, nudge the
      // merchant to set up transaction alerts — only while the account has never been
      // shown that screen (backend display_txn_alert_setting) and has a mobile to alert.
      const account: ICustomer | ISupplier | undefined = yield select(
        (app: IAppState) =>
          app.Dashboard[isCustomer ? 'customers' : 'suppliers'].byIds[entityId] as
            | ICustomer
            | ISupplier
            | undefined
      )
      if (account?.displayTxnAlertSetting && account.mobile) {
        yield put(showTxnAlertNudge({ entityId, entityType }))
      }
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: 'Something went wrong. Please try again!'
        })
      )
      const errorMsg = response.data?.response?.data?.error || ''
      if (errorMsg.includes('exists')) {
        yield put(setTransactionError('This Invoice number already exists!'))
      }
      yield put(addTransactionFailure())
    }
  } catch (e: any) {
    yield put(addTransactionFailure())
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: e.message
      })
    )
  }
}

export function* deleteTransaction(action: Action<ITransactionAction>) {
  const { entityType, entityId, transactionId, deleteAssociatedCNs, redirectTo } = action.payload
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER

  try {
    yield put(setTransactionsLoading(true))

    const { Transactions }: IAppState = yield select((state: IAppState) => state)
    let response: IApiResponse | null = null

    if (
      isCustomer &&
      Transactions.currentTransactions.byIds?.[transactionId]?.referenceSource ===
        REFERENCE_SOURCE.BILLING
    ) {
      response = yield call(deleteBillWithTxnIdReq, { transactionId, deleteAssociatedCNs })
    } else if (
      isCustomer &&
      Transactions.currentTransactions.byIds?.[transactionId]?.referenceSource ===
        REFERENCE_SOURCE.STAFF_LINK
    ) {
      response = yield call(removeStaffLinkTransaction, {
        staffLinkTxnId: Transactions.currentTransactions.byIds?.[transactionId]?.referenceId || '',
        merchantId: Transactions.currentTransactions.byIds?.[transactionId]?.accountId || ''
      })
    } else {
      response = yield call(
        isCustomer
          ? CustomerAndSupplierApi.deleteTransactionForCustomer
          : CustomerAndSupplierApi.deleteTransactionForSupplier,
        transactionId
      )
    }

    if (response?.type === API_RESPONSE_TYPE.SUCCESS) {
      if (redirectTo) {
        yield delay(3000)
        yield call(forwardTo, redirectTo)
      }
      if (response.data.success) {
        yield put(
          addAutoFadeNotification({
            type: NotificationType.SUCCESS,
            bodyText: `${
              isCustomer ? 'Customer' : 'Supplier'
            } transaction was deleted successfully!`
          })
        )
      }
      if (response.data.error) {
        yield put(
          addAutoFadeNotification({
            type: NotificationType.ERROR,
            bodyText: 'Unable to delete transaction'
          })
        )
      }
      // Only mutate local state when the server actually deleted the row. A 200
      // response can still carry `error` ("Unable to delete transaction"); previously
      // the local balance was reversed and the row hidden anyway, diverging the ledger
      // from the server until a refresh un-did it. Guard on the absence of an error.
      if (entityType && entityId && !response.data.error) {
        const txn = Transactions.currentTransactions.byIds?.[transactionId]
        if (txn && !txn.deleted) {
          const delta =
            Number(txn.transactionType) === TRANSACTION_TYPE.PAYMENT
              ? -Number(txn.amount)
              : Number(txn.amount)
          yield put(adjustEntityBalanceLocally({ entityType, entityId, delta }))
        }

        yield put(markTransactionDeletedLocally(transactionId))

        try {
          const dbManager = getTransactionDatabaseManager()
          yield call([dbManager, dbManager.init])
          // Write-through soft-delete: mark this txn deleted in place instead of
          // dropping the account's whole cache (which, with no API call on open,
          // would otherwise leave the ledger unable to render from cache).
          yield call(
            [dbManager, dbManager.softDeleteCachedTransaction],
            entityId,
            entityType,
            transactionId
          )
        } catch (cacheErr) {
          console.error('[deleteTransaction] Cache soft-delete failed:', cacheErr)
        }

        // Re-derive the ledger-list subtitle from the (now soft-deleted) cache. Without
        // this the row keeps describing the transaction that was just deleted — "₹7 Credit
        // Added Today" for a txn that no longer exists — because customerLastActivity is
        // only ever rebuilt by fetchEntities, and a delete does not refetch the list.
        // Add escapes this only by accident: it round-trips through a route change that
        // remounts the ledger.
        yield put(fetchLastActivity(entityType))
      }
      yield put(setTransactionsLoading(false))
    } else {
      yield put(setTransactionsLoading(false))
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText:
            response?.data?.response?.data?.error ||
            response?.data?.message ||
            'Unable to delete transaction'
        })
      )
    }
  } catch (e: any) {
    yield put(setTransactionsLoading(false))
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: e.message
      })
    )
  }
}

export function* generateTransactionReport(
  action: Action<{
    entityType: ENTITY_TYPE
    entityId: string
    filter?: ITransactionFilter
    sourceAction: TRANSACTION_REPORT_ACTION
    language: string
    openingBalance?: number
    closingBalance?: number
  }>
) {
  try {
    const { entityType, entityId, filter, sourceAction, language, openingBalance, closingBalance } =
      action.payload
    if (sourceAction === TRANSACTION_REPORT_ACTION.DOWNLOAD_EXCEL) {
      return
    }
    const state: IAppState = yield select((state: IAppState) => state)
    const payload = {
      device_id: state.Dashboard.merchantProfile.data?.id as string,
      account_id: entityId,
      ...(filter && filter.startTime ? { start_time: filter.startTime } : {}),
      ...(filter && filter.endTime ? { end_time: filter.endTime } : {}),
      lang: language,
      type: entityType === ENTITY_TYPE.CUSTOMER ? 'customer-report' : 'supplier-report'
    }
    const reportUrl = state.Transactions.currentTransactionReport.reportUrl

    if (reportUrl) {
      yield put(generateTransactionReportSuccess(reportUrl))
      showReport(reportUrl, sourceAction)
      return
    }

    const { response, reportAborted }: { response: IApiResponse; reportAborted: any } = yield race({
      response: call(CustomerSupplierReportApi.generateTransactionReportId, payload),
      reportAborted: take([TransactionsActions.FETCH_REPORT_TRANSACTIONS])
    })

    if (reportAborted) {
      yield put(generateTransactionReportFailure())
      return
    }

    const reportId = response.data?.report_id

    if (!reportId) {
      yield put(generateTransactionReportFailure())
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: 'Failed to generate report ID'
        })
      )
      return
    }
    const pollingTimeoutMs = 5 * 60 * 1000
    const pollIntervalMs = 5000
    const startTime = Date.now()
    let reportResponse: IApiResponse | null = null

    while (Date.now() - startTime < pollingTimeoutMs) {
      reportResponse = yield call(CustomerSupplierReportApi.generateTransactionReport, reportId)
      if (reportResponse?.type === API_RESPONSE_TYPE.SUCCESS && reportResponse?.data?.report_url) {
        const reportUrl = reportResponse.data.report_url
        yield put(generateTransactionReportSuccess(reportUrl))
        showReport(reportUrl, sourceAction)
        return
      }
      if (
        reportResponse?.data?.status === 'success' ||
        reportResponse?.type === API_RESPONSE_TYPE.SUCCESS
      ) {
        yield delay(pollIntervalMs)
        continue
      } else {
        break
      }
    }
    yield put(generateTransactionReportFailure())
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText:
          reportResponse?.data?.error_msg ||
          `Failed to generate report within ${(pollingTimeoutMs / 60000).toFixed(0)} minutes`
      })
    )
  } catch (e: any) {
    yield put(generateTransactionReportFailure())
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: e.message || 'An error occurred while generating the report'
      })
    )
  }
}

export function* editTransactionEffect(
  action: Action<{
    payload: any
    entityType: ENTITY_TYPE
    entityId: string
  }>
) {
  try {
    const { entityType, entityId, payload } = action.payload
    yield put(setTransactionsLoading(true))
    const response: IApiResponse = yield call(LedgerApi.editTransaction, payload)
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      // Keep the dashboard balance anchor in sync with an amount edit. The ledger's
      // running balance (and the profile-header balance) is anchored to customer/
      // supplier.balance; unlike create/delete, edit refreshes neither, so without this
      // every running balance stays wrong by the edit delta until an unrelated refresh.
      // Balance contribution is (PAYMENT +amount, CREDIT −amount) for BOTH entity types
      // (only the Advance/Due label flips); op amounts are paise, the balance is rupees.
      const { Transactions }: IAppState = yield select((state: IAppState) => state)
      let balanceDelta = 0
      for (const op of payload?.operations || []) {
        if (op?.transaction && (op.mask || []).includes('amount')) {
          const prev = Transactions.currentTransactions.byIds?.[op.transaction_id]
          if (prev) {
            const oldRupee = Number(prev.amount) || 0
            const newRupee = (Number(op.transaction.amount) || 0) / 100
            const sign = Number(prev.transactionType) === TRANSACTION_TYPE.PAYMENT ? 1 : -1
            balanceDelta += sign * (newRupee - oldRupee)
          }
        }
      }
      // Write-through the edit into IndexedDB from the operation payload. The delta
      // sync can't catch edits (it filters on create_time, which an edit doesn't
      // change) and there is no API call on open, so the cache must be updated here
      // for the ledger to reflect the edit.
      try {
        const operations: any[] = payload?.operations || []
        if (operations.length) {
          const dbManager = getTransactionDatabaseManager()
          yield call([dbManager, 'init'])
          const cached: any[] = yield call(
            [dbManager, 'getTransactionsByEntity'],
            entityId,
            entityType
          )
          const byId = new Map(cached.map((t: any) => [t.id, t]))
          const updates: any[] = []
          for (const op of operations) {
            const existing: any = byId.get(op.transaction_id)
            if (existing && op.transaction) {
              const merged = { ...existing, ...op.transaction }
              if ((op.mask || []).includes('amount')) {
                merged.amount_updated = true
              }
              updates.push(merged)
            }
          }
          if (updates.length) {
            yield call([dbManager, 'bulkUpsertTransactions'], entityType, updates)
            // Re-derive the ledger-list subtitle now that the cache holds the new amount
            // and the amount_updated flag — otherwise the row keeps showing the pre-edit
            // amount and says "Added" instead of "Edited". Must come after the upsert
            // resolves: fetchLastActivity re-reads this same store.
            yield put(fetchLastActivity(entityType))
          }
        }
      } catch (cacheErr) {
        console.warn('[editTransaction] cache write-through failed:', cacheErr)
      }
      if (balanceDelta !== 0) {
        yield put(adjustEntityBalanceLocally({ entityType, entityId, delta: balanceDelta }))
      }
      yield put(
        fetchLatestTransactionsAction({
          entityType,
          entityId
        })
      )
      yield put(toggleEditTransactionAmount(false))
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
    }
    yield put(setTransactionsLoading(false))
  } catch (e: any) {
    yield put(setTransactionsLoading(false))
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e as AxiosError).message
      })
    )
  }
}

// When each account's cache was last re-pulled, so a mismatch that is legitimate
// (a PROCESSING payment, a list that hasn't reloaded yet) can't re-fetch in a loop.
const staleCacheHealedAt = new Map<string, number>()
const STALE_CACHE_HEAL_INTERVAL_MS = 5 * 60 * 1000

/**
 * Self-heal for the cache-only reads. They trust IndexedDB completely and the only
 * thing keeping it fresh is the merchant delta — whose cursor only moves forward, so
 * rows it skips once are never fetched again and the ledger / report stay frozen
 * while the home list (server balance) moves on. The entity's balance is
 * server-authoritative: when the cached ledger doesn't add up to it, re-pull this
 * ONE account's history and merge it in. Returns the fresh rows, or `raw` untouched.
 */
function* healStaleAccountCache(
  entityId: string,
  entityType: ENTITY_TYPE,
  raw: any[]
): Generator<any, any[], any> {
  try {
    const entity: any = yield select((state: IAppState) =>
      entityType === ENTITY_TYPE.CUSTOMER
        ? state.Dashboard.customers?.byIds?.[entityId]
        : state.Dashboard.suppliers?.byIds?.[entityId]
    )
    const serverBalance = Number(entity?.balance)
    if (!entity || !Number.isFinite(serverBalance)) {
      return raw
    }
    const ledger = formatLedgerTransactions(raw, entityType)
    const cachedBalance = ledger.length
      ? Number((ledger[ledger.length - 1] as any).outStandingAmount) || 0
      : 0
    if (Math.abs(cachedBalance - serverBalance) < 0.01) {
      return raw
    }

    const key = `${entityId}_${entityType}`
    const lastHealedAt = staleCacheHealedAt.get(key) || 0
    if (Date.now() - lastHealedAt < STALE_CACHE_HEAL_INTERVAL_MS) {
      return raw
    }
    staleCacheHealedAt.set(key, Date.now())

    // ONE call for the whole history. A reply that isn't an inline list (the backend
    // answers a very large account with a file) goes through the smart sync, which
    // decodes files and caps its own requests — not the year-by-year fallback, whose
    // loop only ends on an empty list and would spin on a failed or file response.
    const response: IApiResponse = yield call(getAccountTransactionsApi, {
      id: entityId,
      orderBy: TRANSACTION_SORT_ORDER.BY_CREATION_TIME,
      role: entityType === ENTITY_TYPE.CUSTOMER ? ROLE.CUSTOMER : ROLE.SUPPLIER,
      is_asc_order: true
    })
    if (response?.type !== API_RESPONSE_TYPE.SUCCESS) {
      return raw
    }
    let allTransactions: any[] = response.data?.list_data?.transactions || []
    if (!allTransactions.length && response.data?.file_data) {
      const syncResult: any = yield call(
        getTransactionSyncService().syncTransactions,
        entityId,
        entityType,
        { orderBy: TRANSACTION_SORT_ORDER.BY_CREATION_TIME }
      )
      allTransactions = syncResult?.success ? syncResult.data || [] : []
    }
    if (!allTransactions.length) {
      return raw
    }

    const dbManager = getTransactionDatabaseManager()
    yield call([dbManager, 'init'])
    const businessId: string | undefined =
      (yield AuthStorageInstance.getCurrentBusinessId()) || undefined
    const recovered: number = yield call(
      [dbManager, 'bulkUpsertTransactions'],
      entityType,
      allTransactions.map((t: any) => ({ ...t, account_id: t.account_id || entityId })),
      businessId
    )
    Mixpanel.track('txn_cache_healed', {
      account_id: entityId,
      entity_type: entityType === ENTITY_TYPE.CUSTOMER ? 'customer' : 'supplier',
      cached_balance: cachedBalance,
      server_balance: serverBalance,
      cached_count: raw.length,
      recovered_count: recovered
    })
    if (!recovered) {
      return raw
    }
    // The list subtitle ("Payment Added on …") is derived from this same cache.
    yield put(fetchLastActivity(entityType))
    return yield call([dbManager, 'getTransactionsByEntity'], entityId, entityType)
  } catch (healErr) {
    console.warn('[healStaleAccountCache] failed:', healErr)
    return raw
  }
}

export function* fetchLatestTransactions(
  action: Action<{
    entityType: ENTITY_TYPE
    entityId: string
    filter?: ITransactionFilter
    source?: string
    limit?: number
    endTime?: number
  }>
) {
  const { entityType, entityId, filter, endTime } = action.payload
  try {
    // Cache-only open: render straight from IndexedDB for a fully-seeded account on
    // a plain open (no date filter / pagination) — NO GetTransactions call. Cross-
    // device freshness comes from the reload delta + the manual Refresh button.
    const canServeFromCache = !(filter && filter.startTime) && !(Number(endTime) > 1)
    if (canServeFromCache) {
      try {
        const dbManager = getTransactionDatabaseManager()
        yield call([dbManager, 'init'])
        const hasCache: boolean = yield call([dbManager, 'hasData'], entityId, entityType)
        const metadata: any = yield call([dbManager, 'getMetadata'], entityId, entityType)
        if (hasCache && metadata?.hasCompletedFullFetch) {
          const raw: any[] = yield call(
            [dbManager, 'getTransactionsByEntity'],
            entityId,
            entityType
          )
          const transactions = formatLedgerTransactions(raw, entityType)
          yield put(fetchLatestTransactionsSuccess({ ...action.payload, data: transactions }))
          // Rendered from cache already; only a cache that disagrees with the server
          // balance costs a network call, and the ledger re-renders once it lands.
          const healed: any[] = yield call(healStaleAccountCache, entityId, entityType, raw)
          if (healed !== raw) {
            yield put(
              fetchLatestTransactionsSuccess({
                ...action.payload,
                data: formatLedgerTransactions(healed, entityType)
              })
            )
          }
          return
        }
      } catch (cacheErr: any) {
        console.warn(
          '[fetchLatestTransactions] cache read failed, falling back to API:',
          cacheErr?.message
        )
      }
    }

    // Uncached (never seeded) / filtered / paginated → network fetch.
    const { response } = yield call(fetchAllTransactions, {
      entityId,
      entityType,
      ...(filter && filter.startTime ? { start_time: filter.startTime } : {}),
      orderBy: TRANSACTION_SORT_ORDER.BY_CREATION_TIME
    })
    if (
      response.type === API_RESPONSE_TYPE.SUCCESS &&
      response.data &&
      Object.keys(response.data).length > 0
    ) {
      if (response.data.list_data.transactions) {
        const transactions = formatLedgerTransactions(
          response.data.list_data.transactions,
          entityType
        )
        yield put(fetchLatestTransactionsSuccess({ ...action.payload, data: transactions }))
      }
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.message
        })
      )
      yield put(fetchLatestTransactionsFailure(entityType, entityId))
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message
      })
    )
    yield put(fetchLatestTransactionsFailure(entityType, entityId))
  }
}

export function* fetchLatestTransactionsForReport(
  action: Action<{
    entityType: ENTITY_TYPE
    entityId: string
    filter?: ITransactionFilter
    source?: string
    limit?: number
    endTime?: number
  }>
) {
  const { entityType, entityId, filter, endTime } = action.payload
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER

  try {
    const { response, allTransactions } = yield call(fetchAllTransactions, {
      entityId,
      entityType,
      filter,
      endTime,
      orderBy: TRANSACTION_SORT_ORDER.BY_CREATION_TIME
    })

    if (response.type === API_RESPONSE_TYPE.SUCCESS && allTransactions?.length > 0) {
      let sum = 0
      let supplierSum = 0
      const transactions = (
        isCustomer
          ? allTransactions.map((transaction: any) => {
              const formatted = getFormattedCustomerTransaction(transaction, sum)
              sum = formatted.outStandingAmount
              return formatted
            })
          : allTransactions.map((supplierTransaction: any) => {
              const formatted = getFormattedSupplierTransaction(supplierTransaction, supplierSum)
              supplierSum = formatted.outStandingAmount
              return formatted
            })
      ).sort((a: any, b: any) => Number(a.bill_date) - Number(b.bill_date))

      yield put(
        fetchLatestTransactionsSuccess({
          ...action.payload,
          data: transactions
        })
      )
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data?.message || 'No Transactions Found'
        })
      )
      yield put(fetchLatestTransactionsFailure(entityType, entityId))
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e).message
      })
    )
    yield put(fetchLatestTransactionsFailure(entityType, entityId))
  }
}

function* fetchAllTransactions({
  entityId,
  entityType,
  filter,
  orderBy,
  endTime
}: {
  entityId: string
  entityType: ENTITY_TYPE
  filter?: ITransactionFilter
  orderBy: TRANSACTION_SORT_ORDER
  endTime?: number
}): Generator<any, { response: IApiResponse; allTransactions: any[] }, any> {
  try {
    // Import the smart sync service
    const syncService = getTransactionSyncService()

    // Use smart sync with simplified interface
    const syncResult: any = yield call(syncService.syncTransactions, entityId, entityType, {
      orderBy
    })

    if (syncResult.success) {
      // Return cached data
      return {
        response: {
          type: API_RESPONSE_TYPE.SUCCESS,
          data: { list_data: { transactions: syncResult.data } }
        },
        allTransactions: syncResult.data
      }
    }

    // Fallback to original implementation if smart sync fails
    console.warn('Smart sync failed, falling back to direct API call:', syncResult.error)
    return yield call(fetchAllTransactionsFallback, {
      entityId,
      entityType,
      filter,
      orderBy,
      endTime
    })
  } catch (error) {
    console.error('Error in fetchAllTransactions with smart sync:', error)
    // Fallback to original implementation
    return yield call(fetchAllTransactionsFallback, {
      entityId,
      entityType,
      filter,
      orderBy,
      endTime
    })
  }
}

function* fetchAllTransactionsFallback({
  entityId,
  entityType,
  filter,
  orderBy,
  endTime
}: {
  entityId: string
  entityType: ENTITY_TYPE
  filter?: ITransactionFilter
  orderBy: TRANSACTION_SORT_ORDER
  endTime?: number
}): Generator<any, { response: IApiResponse; allTransactions: any[] }, any> {
  const isCustomer = entityType === ENTITY_TYPE.CUSTOMER
  let allTransactions: any[] = []
  let response: IApiResponse = yield call(getAccountTransactionsApi, {
    id: entityId,
    ...(filter?.startTime ? { start_time: filter.startTime } : {}),
    ...(endTime || filter?.endTime ? { end_time: endTime || filter?.endTime } : {}),
    orderBy,
    role: isCustomer ? ROLE.CUSTOMER : ROLE.SUPPLIER,
    is_asc_order: true
  })

  allTransactions = [...(response.data?.list_data?.transactions || [])]

  if (
    response.type === API_RESPONSE_TYPE.SUCCESS &&
    response.data &&
    (!response.data.list_data || response.data.list_data === null)
  ) {
    let hasMoreData = true
    let count = 0
    while (hasMoreData) {
      response = yield call(getAccountTransactionsApi, {
        id: entityId,
        start_time: dayjs()
          .subtract(count + 1, 'year')
          .unix(),
        end_time: dayjs().subtract(count, 'year').unix(),
        orderBy,
        role: isCustomer ? ROLE.CUSTOMER : ROLE.SUPPLIER
      })
      allTransactions = [...allTransactions, ...(response.data?.list_data?.transactions || [])]
      count++
      if (response.data?.list_data?.transactions?.length === 0) {
        hasMoreData = false
      }
    }
  }

  return { response, allTransactions }
}

export function* fetchDoubleTransactions(
  action: Action<{
    transactionId: string
    accountId: string
  }>
) {
  try {
    const { transactionId, accountId } = action.payload
    const currentBusinessId: string = yield AuthStorageInstance.getCurrentBusinessId()
    const payload = {
      business_id: currentBusinessId,
      entry_id: transactionId,
      account_id: accountId
    }
    const response: IApiResponse = yield call(getDoubleEntry, payload)
    if (response.type === API_RESPONSE_TYPE.SUCCESS) {
      const { account_name, reference, deleted } = response.data
      yield put(saveDoubleEntryTransactionDetails({ reference, account_name, deleted }))
    } else {
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: response.data.response.data.message
        })
      )
    }
  } catch (e: any) {
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: getErrorMessage(e as AxiosError).message
      })
    )
  }
}

export function* syncTransactionsWithIndexDBEffect(
  action: Action<{
    entityType: ENTITY_TYPE
    entityId: string
    filter?: ITransactionFilter
    orderBy?: number
    endTime?: number
    forceRefresh?: boolean
  }>
): Generator<any, void, any> {
  try {
    const { entityType, entityId, filter, orderBy, endTime, forceRefresh } = action.payload

    const syncService = getTransactionSyncService()

    // Sync transactions with IndexDB
    const syncResult: any = yield call(syncService.syncTransactions, entityId, entityType, {
      orderBy: orderBy || 3
    })

    if (syncResult.success) {
      yield put(
        syncTransactionsWithIndexDBSuccess({
          entityType,
          entityId,
          data: syncResult.data
        })
      )

      // Show notification for successful sync
      yield put(
        addAutoFadeNotification({
          type: NotificationType.SUCCESS,
          bodyText: `Synced ${syncResult.data.length} transactions`
        })
      )
    } else {
      yield put(
        syncTransactionsWithIndexDBFailure({
          entityType,
          entityId,
          error: syncResult.error || 'Unknown error'
        })
      )

      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: `Failed to sync transactions: ${syncResult.error}`
        })
      )
    }
  } catch (error: any) {
    console.error('Error in syncTransactionsWithIndexDBEffect:', error)
    yield put(
      syncTransactionsWithIndexDBFailure({
        entityType: action.payload.entityType,
        entityId: action.payload.entityId,
        error: error.message || 'Unknown error'
      })
    )

    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: `Failed to sync transactions: ${error.message}`
      })
    )
  }
}

/**
 * Pull-to-refresh: `type:0` GetTransactions calls fetch every CUSTOMER and
 * SUPPLIER transaction changed since each type's business-wide `update_time`
 * cursor and merge the deltas into IndexedDB. On success it reloads both the
 * customer and supplier lists so balances reflect the new data, and reloads the
 * currently-open ledger (customer or supplier) if one is open.
 */
export function* refreshMerchantTransactionsEffect(
  action?: Action<{ entityType?: ENTITY_TYPE } | null>
): Generator<any, void, any> {
  try {
    // A realtime push names the affected side (customer_txns / supplier_txns), so
    // refresh only that type. No entityType (manual refresh / seed / boot) → both.
    const targetType = action?.payload?.entityType
    const entityTypes = targetType ? [targetType] : [ENTITY_TYPE.CUSTOMER, ENTITY_TYPE.SUPPLIER]

    const syncService = getTransactionSyncService()
    const result: any = yield call(syncService.refreshMerchantTransactions, entityTypes)

    if (!result.success) {
      yield put(refreshMerchantTransactionsFailure())
      yield put(
        addAutoFadeNotification({
          type: NotificationType.ERROR,
          bodyText: 'Could not refresh transactions. Please try again.'
        })
      )
      return
    }

    yield put(refreshMerchantTransactionsSuccess(result.count))

    // Refresh only the affected home list(s) so balances reflect the new transactions.
    for (const t of entityTypes) {
      yield put(fetchEntityList(t))
    }

    // If a ledger is currently open and belongs to a refreshed type, reload it from cache.
    const { currentEntity }: ITransactionsState = yield select(
      (state: IAppState) => state.Transactions
    )
    if (currentEntity && entityTypes.includes(currentEntity.type)) {
      yield put(
        fetchLatestTransactionsAction({
          entityType: currentEntity.type,
          entityId: currentEntity.id
        })
      )
    }

    // yield put(
    //   addAutoFadeNotification({
    //     type: result.partial ? NotificationType.INFO : NotificationType.SUCCESS,
    //     bodyText: result.count > 0 ? `Updated ${result.count} transactions` : 'You are up to date'
    //   })
    // )
  } catch (error: any) {
    console.error('Error in refreshMerchantTransactionsEffect:', error)
    yield put(refreshMerchantTransactionsFailure())
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: 'Could not refresh transactions. Please try again.'
      })
    )
  }
}

export function* clearTransactionCacheEffect(
  action: Action<{
    entityType: ENTITY_TYPE
    entityId: string
  }>
): Generator<any, void, any> {
  try {
    const { entityType, entityId } = action.payload

    const syncService = getTransactionSyncService()

    // Clear cache for the specific entity
    yield call(syncService.clearCache, entityId, entityType)

    yield put(
      addAutoFadeNotification({
        type: NotificationType.SUCCESS,
        bodyText: 'Transaction cache cleared successfully'
      })
    )
  } catch (error: any) {
    console.error('Error in clearTransactionCacheEffect:', error)
    yield put(
      addAutoFadeNotification({
        type: NotificationType.ERROR,
        bodyText: `Failed to clear cache: ${error.message}`
      })
    )
  }
}
